// Global Types
import type {
  $FullAdminRegattaResultOverall,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  RegattaResultOverallType,
} from 'common.data';
import {
  adminPages,
  adminSections,
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
  regattaResultOverallFilterLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
  publicBorder,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaResultOverallFields from '../../fields/RegattaResultOverallFields';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullAdminRegattaResultOverall>;
  loading: boolean;
  resourceData: Array<$Model<'RegattaResultTable'>>;
};

const pageId = adminPages.regattaResultOverallsPage;

class RegattaResultOverallsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceData: [],
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId) {
          if (data.modelName === 'RegattaResultOverall') {
            this.getData();
          }

          if (data.modelName === 'RegattaResultTable') {
            this.getResourceData();
          }
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegattaResultOverall({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const resourceData = await Services.Api.getResourceRegattaResultTable({
            RegattaId,
          });

          await this.setStateAsync({
            resourceData,
          });
        }
      },
    });
  }

  renderHeader() {
    const {
      main,
      redirect,
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const RegattaId = main.activeRegattaId;

    if (RegattaId !== null) {
      const newItem: $ModelUpsertParams<'RegattaResultOverall'> & {
        RegattaResultTables: Array<$Model<'RegattaResultTable'>>;
      } = {
        Filter: null,
        Name: '',
        Public: false,
        RegattaId,
        RegattaResultTables: [],
        Type: RegattaResultOverallType.et,
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            iconClass={'icon-shuffle'}
            rightButtons={(
              <FormDialog
                fields={(
                  <RegattaResultOverallFields
                    item={newItem}
                    resourceData={resourceData}
                  />
                )}
                id={'create-regatta-result-overall-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$FullAdminRegattaResultOverall>({
                      ...newItem,
                      ...values,
                      RegattaResultTables: _.filter(
                        resourceData,
                        (i) => _.includes(
                          values.RegattaResultTables as Array<string>,
                          i.id,
                        ),
                      ),
                    });

                    await Services.Api.upsertRegattaResultOverall(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Regatta overall result')}
              >
                <Button
                  color={'info'}
                  id={'create-regatta-result-overall-button'}
                  size={'sm'}
                  title={t('Add new overall result table')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Regatta overall results')}
            titleButton={(
              <Button
                className={'mr-2'}
                color={'secondary'}
                id={'redirect-regatta-reults-page-button'}
                onClick={() => redirect({
                  pathname: '/regatta/results',
                })}
                size={'sm'}
              >
                <em className={'fa icon-arrow-left'} />
              </Button>
            )}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $FullAdminRegattaResultOverall) {
    const {
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaResultOverall,
            docType: docType.pdf,
            id,
            language: i18next.language as $Language,
          })}
          id={`regatta-overall-result-file-${id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <FormDialog
          fields={(
            <RegattaResultOverallFields
              item={dataItem}
              resourceData={resourceData}
            />
          )}
          id={`update-regatta-overall-result-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$FullAdminRegattaResultOverall>({
                ...dataItem,
                ...values,
                RegattaResultTables: _.filter(
                  resourceData,
                  (i) => _.includes(
                    values.RegattaResultTables as Array<string>,
                    i.id,
                  ),
                ),
              });

              await Services.Api.upsertRegattaResultOverall(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta overall result')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`delete-regatta-overall-result-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-overall-result-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaResultOverall(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta overall result')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-overall-result-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Type')}</th>
            <th>{t('Regatta result tables')}</th>
            <th>{t('Filter')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $FullAdminRegattaResultOverall) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Name}</td>
              <td>{dataItem.Type}</td>
              <td>
                {_.join(
                  _.map(
                    dataItem.RegattaResultTables,
                    'Name',
                  ),
                  ', ',
                )}
              </td>
              <td>
                {dataItem.Filter ? regattaResultOverallFilterLabel[dataItem.Filter] : ''}
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaResultOverallsPageView;
